import { Box } from '@chakra-ui/react';
import { useAppSelector } from '@hooks/redux.hooks';
import { selectSelectedNotificationId } from '@redux/global/global.selector';
import { memo, useEffect, useMemo, useRef } from 'react';
import AutoSizer from 'react-virtualized-auto-sizer';
import { FixedSizeList, ListChildComponentProps } from 'react-window';

import NotificationCard from './card/NotificationCard';

type RowProps = {
  notificationIds: string[];
  hideProcedure: boolean;
};

const Row = ({ index, style, data }: ListChildComponentProps<RowProps>) => {
  const notificationId = data.notificationIds[index];
  return (
    <NotificationCard
      style={style}
      key={notificationId}
      isLast={index === data.notificationIds.length - 1}
      notificationId={notificationId}
      hideProcedure={data.hideProcedure}
    />
  );
};

type Props = {
  notificationIds: string[];
  hideProcedure?: boolean;
};

function LiveNotificationList({ notificationIds, hideProcedure = false }: Readonly<Props>) {
  const selectedNotificationId = useAppSelector(selectSelectedNotificationId);
  const memoizedData = useMemo(() => ({ notificationIds, hideProcedure }), [hideProcedure, notificationIds]);
  const listRef = useRef<FixedSizeList | null>(null);
  useEffect(() => {
    if (listRef.current && selectedNotificationId) {
      listRef.current.scrollToItem(memoizedData.notificationIds.indexOf(selectedNotificationId), 'smart');
    }
  }, [memoizedData.notificationIds, selectedNotificationId]);

  return (
    <AutoSizer style={{ width: '100%', height: 'fit-content', position: 'relative' }} disableWidth>
      {({ height }) => {
        const totalListHeight = notificationIds.length * 130;
        const isOverflowing = totalListHeight > height;
        return (
          <>
            <FixedSizeList
              width="100%"
              ref={listRef}
              itemKey={(index) => notificationIds[index]}
              height={isOverflowing ? height : totalListHeight}
              itemCount={notificationIds.length}
              style={{ flexShrink: 0 }}
              itemSize={130}
              itemData={memoizedData}
              className="cockpit-scrollbar no-margin"
            >
              {Row}
            </FixedSizeList>
            <Box
              position="absolute"
              backgroundColor="neutral.700"
              top={totalListHeight + 2}
              height={`${height - totalListHeight - 2}px`}
              width="100%"
            />
          </>
        );
      }}
    </AutoSizer>
  );
}

export default memo(LiveNotificationList);
